import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import {
  ArrowLeftIcon,
  CameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { apiGet, apiGetBlob, apiPost, apiUpload } from "../api/client";
import UpgradeModal from "../components/UpgradeModal";
import { AppleDateInput } from "../components/AppleDateInput";
import { usePlan } from "../hooks/usePlan";
import { A } from "../styles/appleClasses";
import { companyRoute } from "../utils/routeHelpers";
import "./Scanner.css";
import {
  createScannerHandoff,
  type ScannerRouteState,
} from "../types/scannerHandoff";

type CaptureMode = "camera" | "upload";
type ScannerTarget = "expense" | "purchase";

type ScannerErrorKey =
  | "scannerPage.pdfUnsupported"
  | "scannerPage.fileTypeUnsupported"
  | "scannerPage.imageUnreadable"
  | "scannerPage.ocrIncomplete"
  | "scannerPage.genericError"
  | "scannerPage.cameraPermissionDenied"
  | "scannerPage.cameraUnavailable"
  | "scannerPage.cameraNotReady"
  | "scannerPage.captureFailed"
  | "scannerPage.imageCreationFailed";
type ScannerError = { key: ScannerErrorKey } | string;
const SUPPORTED_UPLOAD_TYPES = new Set(["image/jpeg", "image/png"]);

type InitialScannerSuggestions = {
  sourceDocumentId: number;
  vendor: string | null;
  date: string | null;
  amount: number | null;
  currency: string | null;
};

type SourceDocumentRead = {
  id: number;
  company_id: number;
  uploaded_by_user_id?: number | null;
  document_type_hint?: string | null;
  status: string;
  storage_provider: string;
  original_filename?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  meta?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
};

type SourceDocumentOcrSuggestionRead = {
  id: number;
  company_id: number;
  source_document_id: number;
  provider?: string | null;
  provider_job_id?: string | null;
  status: string;
  suggested_vendor_name?: string | null;
  suggested_date?: string | null;
  suggested_total_amount?: number | null;
  suggested_currency?: string | null;
  extracted?: Record<string, unknown> | null;
  confidence?: Record<string, unknown> | null;
  warnings?: string[] | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
};

function extractApiDetail(err: any): ScannerError {
  return (
    err?.response?.data?.detail ||
    err?.message ||
    { key: "scannerPage.genericError" }
  );
}

function formatSuggestedAmount(value?: number | null): string {
  if (value === null || value === undefined) return "";
  return Number(value).toFixed(2);
}

function normalizeText(value?: string | null): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function normalizeYmd(value?: string | null): string | null {
  const normalized = normalizeText(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day)
    ? normalized
    : null;
}

function normalizeAmount(value: string): number | null | "invalid" {
  const normalized = value.trim();
  if (!normalized) return null;

  const validFormat =
    /^[+-]?(?:(?:\d{1,3}(?:,\d{3})+)|\d+)(?:\.\d+)?$/.test(normalized) ||
    /^[+-]?\.\d+$/.test(normalized);
  if (!validFormat) return "invalid";

  const amount = Number(normalized.replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : "invalid";
}

function getRawOcrText(extracted?: Record<string, unknown> | null): string {
  const rawText = extracted?.raw_text;
  return typeof rawText === "string" ? rawText.trim() : "";
}

function isNarrowScreen(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

export default function Scanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { company_id } = useParams<{ company_id: string }>();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraStartIdRef = useRef(0);
  const cameraStartingRef = useRef(false);
  const autoStartedCameraRef = useRef(false);
  const initializedSuggestionIdRef = useRef<number | null>(null);
  const initialSuggestionsRef = useRef<InitialScannerSuggestions | null>(null);
  const handoffStartedRef = useRef(false);

  const { isPro, loading: planLoading } = usePlan();
  const planReady = !planLoading;
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [captureMode, setCaptureMode] = useState<CaptureMode>(
    isNarrowScreen() ? "camera" : "upload",
  );
  const [target, setTarget] = useState<ScannerTarget>("expense");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceDocument, setSourceDocument] =
    useState<SourceDocumentRead | null>(null);
  const [suggestion, setSuggestion] =
    useState<SourceDocumentOcrSuggestionRead | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<ScannerError | null>(null);
  const [companyCurrency, setCompanyCurrency] = useState("HKD");
  const [vendorDraft, setVendorDraft] = useState("");
  const [dateDraftYmd, setDateDraftYmd] = useState("");
  const [amountDraft, setAmountDraft] = useState("");

  const isPdf =
    (sourceDocument?.content_type || "").toLowerCase() === "application/pdf";

  const isImage = (sourceDocument?.content_type || "")
    .toLowerCase()
    .startsWith("image/");

  const hasPreview = !!previewUrl && !!sourceDocument;

  const rawOcrText = getRawOcrText(suggestion?.extracted);
  const detectedCurrency =
    suggestion?.suggested_currency?.trim().toUpperCase() ?? "";
  const showFxNote = !!detectedCurrency && detectedCurrency !== companyCurrency;
  const scannerHandoff = createScannerHandoff({
    vendorText: vendorDraft,
    dateYmd: dateDraftYmd,
    amount: amountDraft,
    sourceDocumentId: sourceDocument?.id,
  });

  useEffect(() => {
    if (!company_id) return;

    let cancelled = false;
    apiGet<{ value?: string | null }>(
      `/settings/currency?company_id=${company_id}`,
    )
      .then((response) => {
        const currency = response?.value?.trim().toUpperCase();
        if (!cancelled && currency) setCompanyCurrency(currency);
      })
      .catch(() => {
        // Keep Nimos's existing HKD default when settings are unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [company_id]);

  useEffect(() => {
    if (!suggestion) {
      initializedSuggestionIdRef.current = null;
      initialSuggestionsRef.current = null;
      setVendorDraft("");
      setDateDraftYmd("");
      setAmountDraft("");
      return;
    }

    if (initializedSuggestionIdRef.current !== suggestion.id) {
      initializedSuggestionIdRef.current = suggestion.id;
      setVendorDraft(suggestion.suggested_vendor_name ?? "");
      setDateDraftYmd(normalizeYmd(suggestion.suggested_date) ?? "");
      setAmountDraft(formatSuggestedAmount(suggestion.suggested_total_amount));
    }

    if (
      sourceDocument?.id === suggestion.source_document_id &&
      initialSuggestionsRef.current?.sourceDocumentId !== sourceDocument.id
    ) {
      initialSuggestionsRef.current = {
        sourceDocumentId: sourceDocument.id,
        vendor: suggestion.suggested_vendor_name ?? null,
        date: suggestion.suggested_date ?? null,
        amount: suggestion.suggested_total_amount ?? null,
        currency: suggestion.suggested_currency ?? null,
      };
    }
  }, [sourceDocument?.id, suggestion]);

  function openAccountingEntry(path: "/expenses/new" | "/purchases/new") {
    if (!scannerHandoff) return;
    if (handoffStartedRef.current) return;
    handoffStartedRef.current = true;

    const initial = initialSuggestionsRef.current;
    if (initial && initial.sourceDocumentId === sourceDocument?.id) {
      const initialVendor = normalizeText(initial.vendor);
      const initialDate = normalizeYmd(initial.date);
      const initialAmount = initial.amount;
      const initialCurrency = normalizeText(initial.currency).toUpperCase();
      const currentAmount = normalizeAmount(amountDraft);

      const changed = {
        vendor: initialVendor !== normalizeText(vendorDraft),
        date: initialDate !== normalizeYmd(dateDraftYmd),
        amount:
          initialAmount === null
            ? currentAmount !== null
            : currentAmount === "invalid" ||
              currentAmount === null ||
              initialAmount !== currentAmount,
        currency:
          initialCurrency !== normalizeText(companyCurrency).toUpperCase(),
      };
      const fieldsChangedCount = Object.values(changed).filter(Boolean).length;

      try {
        posthog?.capture("scanner_handoff", {
          destination: path === "/expenses/new" ? "expense" : "purchase",
          source_document_id: initial.sourceDocumentId,
          vendor_detected: initialVendor !== "",
          date_detected: initialDate !== null,
          amount_detected:
            initialAmount !== null && Number.isFinite(initialAmount),
          currency_detected: initialCurrency !== "",
          vendor_changed: changed.vendor,
          date_changed: changed.date,
          amount_changed: changed.amount,
          currency_changed: changed.currency,
          fields_changed_count: fieldsChangedCount,
          manual_intervention_required: fieldsChangedCount > 0,
        });
      } catch {
        // Analytics must never interrupt the existing scanner handoff.
      }
    }

    const state: ScannerRouteState = { scannerHandoff };
    navigate(companyRoute(company_id, path), { state });
  }

  const stopCamera = useCallback(() => {
    cameraStartIdRef.current += 1;
    cameraStartingRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {
        // Best effort cleanup only.
      }
      videoRef.current.srcObject = null;
    }

    setCameraOpen(false);
    setCameraStarting(false);
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(companyRoute(company_id, "/dashboard"));
  }, [company_id, navigate, stopCamera]);

  const handleBack = useCallback(() => {
    stopCamera();
    navigate(-1);
  }, [navigate, stopCamera]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (captureMode === "upload") stopCamera();
  }, [captureMode, stopCamera]);

  async function loadPreview(doc: SourceDocumentRead) {
    setPreviewLoading(true);
    setError(null);
    try {
      const blob = await apiGetBlob(`/source-documents/${doc.id}/preview`);
      const nextUrl = URL.createObjectURL(blob);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return nextUrl;
      });
    } catch (err: any) {
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      setError(extractApiDetail(err));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function runOcr(doc: SourceDocumentRead) {
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }

    setReading(true);
    setError(null);

    try {
      const next = await apiPost<SourceDocumentOcrSuggestionRead>(
        `/source-documents/${doc.id}/ocr`,
        {},
      );

      if (next.status !== "succeeded") {
        setSuggestion(null);
        setSourceDocument((prev) =>
          prev
            ? {
                ...prev,
                status:
                  next.status === "pending" || next.status === "processing"
                    ? "ocr_pending"
                    : "ocr_failed",
                updated_at: new Date().toISOString(),
              }
            : prev,
        );

        const backendMessage = next.error_message?.trim();
        setError(
          next.status === "failed"
            ? !backendMessage || /^bad image data\.?$/i.test(backendMessage)
              ? { key: "scannerPage.imageUnreadable" }
              : backendMessage
            : { key: "scannerPage.ocrIncomplete" },
        );
        return;
      }

      setSuggestion(next);
      setSourceDocument((prev) =>
        prev
          ? {
              ...prev,
              status: "ocr_succeeded",
              updated_at: new Date().toISOString(),
            }
          : prev,
      );
    } catch (err: any) {
      const msg = extractApiDetail(err);
      setError(msg);
      if (typeof msg === "string" && /pro plan|required|402/i.test(msg)) setUpgradeOpen(true);
    } finally {
      setReading(false);
    }
  }

  async function uploadFile(file: File) {
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }

    setUploading(true);
    setError(null);
    setSuggestion(null);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_type_hint", "receipt");

      const uploaded = await apiUpload<SourceDocumentRead>(
        "/source-documents",
        form,
      );

      setSourceDocument(uploaded);
      await loadPreview(uploaded);
      await runOcr(uploaded);
    } catch (err: any) {
      const msg = extractApiDetail(err);
      setError(msg);
      if (typeof msg === "string" && /pro plan|required|402/i.test(msg)) setUpgradeOpen(true);
    } finally {
      setUploading(false);
    }
  }

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraUnavailable(true);
      setCaptureMode("upload");
      return;
    }

    if (cameraStartingRef.current) return;

    stopCamera();
    const startId = cameraStartIdRef.current + 1;
    cameraStartIdRef.current = startId;
    cameraStartingRef.current = true;

    setCameraStarting(true);
    setCameraUnavailable(false);
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      if (cameraStartIdRef.current !== startId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("Camera preview is unavailable.");
      }

      streamRef.current = stream;
      video.srcObject = null;
      video.srcObject = stream;
      await video.play();

      setCameraOpen(true);
    } catch (err: any) {
      if (cameraStartIdRef.current !== startId) return;
      stopCamera();
      setCameraUnavailable(true);
      setCaptureMode("upload");
      setError(
        err?.name === "NotAllowedError"
          ? { key: "scannerPage.cameraPermissionDenied" }
          : { key: "scannerPage.cameraUnavailable" },
      );
    } finally {
      if (cameraStartIdRef.current === startId) {
        cameraStartingRef.current = false;
        setCameraStarting(false);
      }
    }
  }, [stopCamera]);

  useEffect(() => {
    if (!planReady || !isPro) return;
    if (!isNarrowScreen()) return;
    if (autoStartedCameraRef.current) return;

    autoStartedCameraRef.current = true;
    setCaptureMode("camera");
    void startCamera();
  }, [isPro, planReady, startCamera]);

  function resetDocumentState() {
    initialSuggestionsRef.current = null;
    handoffStartedRef.current = false;
    setSourceDocument(null);
    setSuggestion(null);
    setError(null);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    resetDocumentState();

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!SUPPORTED_UPLOAD_TYPES.has(file.type.toLowerCase())) {
      setSelectedFile(null);
      setError(
        file.type.toLowerCase() === "application/pdf"
          ? { key: "scannerPage.pdfUnsupported" }
          : { key: "scannerPage.fileTypeUnsupported" },
      );
      return;
    }

    setSelectedFile(file);
    void uploadFile(file);
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setError({ key: "scannerPage.cameraNotReady" });
      return;
    }

    const canvas = window.document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError(
        { key: "scannerPage.captureFailed" },
      );
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.92);
    });

    if (!blob) {
      setError(
        { key: "scannerPage.imageCreationFailed" },
      );
      return;
    }

    const file = new File([blob], `receipt-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });

    setSelectedFile(file);
    resetDocumentState();
    stopCamera();
    await uploadFile(file);
  }

  function switchToUploadMode() {
    setCaptureMode("upload");
    stopCamera();
  }

  function switchToCameraMode() {
    setCaptureMode("camera");
    void startCamera();
  }

  function useCameraFromUpload() {
    resetDocumentState();
    setSelectedFile(null);
    switchToCameraMode();
  }

  function retakePhoto() {
    resetDocumentState();
    setSelectedFile(null);
    setCaptureMode("camera");
    void startCamera();
  }

  if (planReady && !isPro) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body} px-4 py-8`}>
        <div className="mx-auto max-w-3xl">
          <div className="scanner-safe-header sticky mb-4 border-b border-gray-100 bg-gray-50 px-2 pb-2">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                className={`${A.iconBtn} text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                aria-label={t("directory.common.back")}
              >
                <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>

              <div className="min-w-0 flex-1 text-center">
                <h1 className={A.title}>{t("navigation.scanner")}</h1>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className={`${A.iconBtn} text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                aria-label={t("directory.common.close")}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className={`${A.card} p-6 text-center`}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-appleblue/10 text-2xl">
              ✦
            </div>
            <h2 className="text-[15px] font-semibold text-gray-700">
              {t("scannerPage.proRequired")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-gray-500">
              {t("scannerPage.proDescription")}
            </p>
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              className={`${A.btnBase} ${A.btnSecondary} mt-5`}
            >
              {t("sales.upgrade.title")}
            </button>
          </div>
        </div>
        <UpgradeModal
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} px-4 py-6 sm:py-8`}>
      <div className="mx-auto max-w-3xl">
        <div className="scanner-safe-header sticky mb-4 border-b border-gray-100 bg-gray-50 px-2 pb-2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className={`${A.iconBtn} text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
              aria-label={t("directory.common.back")}
            >
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            <div className="min-w-0 flex-1 text-center">
              <h1 className={A.title}>{t("navigation.scanner")}</h1>
              <p className="mt-0.5 truncate text-xs text-gray-500">
                {t("scannerPage.description")}
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className={`${A.iconBtn} text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
              aria-label={t("directory.common.close")}
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="space-y-4 lg:space-y-0 lg:rounded-2xl lg:border lg:border-gray-200 lg:bg-white lg:p-8 lg:shadow-sm">
          <section
            className={`${A.card} p-4 lg:rounded-none lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-[13px] font-medium text-gray-600">
                  {t("scannerPage.addReceipt")}
                </h2>
              </div>

              {captureMode === "camera" ? (
                <button
                  type="button"
                  onClick={switchToUploadMode}
                  className={`${A.btnBase} ${A.btnSecondary} whitespace-nowrap`}
                  disabled={uploading || reading}
                >
                  {t("scannerPage.upload")}
                </button>
              ) : sourceDocument?.status === "linked" ||
                sourceDocument?.status === "archived" ? null : (
                <button
                  type="button"
                  onClick={useCameraFromUpload}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-transparent text-appleblue transition hover:bg-appleblue/10 focus:outline-none focus:ring-2 focus:ring-appleblueD disabled:opacity-50"
                  disabled={uploading || reading || cameraStarting}
                  aria-label={t("scannerPage.useCamera")}
                  title={t("scannerPage.useCamera")}
                >
                  <CameraIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>

            {captureMode === "camera" ? (
              <div className="scanner-preview-boundary rounded-3xl border border-gray-200 bg-black">
                <div className="relative aspect-[3/4] max-h-[70vh] w-full sm:aspect-video">
                  {hasPreview && isImage ? (
                    <img
                      src={previewUrl}
                      alt={t("scannerPage.capturedPreview")}
                      className="h-full w-full object-contain bg-black"
                    />
                  ) : hasPreview && isPdf ? (
                    <object
                      data={previewUrl}
                      type="application/pdf"
                      className="h-full w-full bg-white"
                    >
                      <div className="flex h-full items-center justify-center bg-white p-4 text-center text-xs text-gray-500">
                        {t("purchases.detail.pdfUnavailable")}
                      </div>
                    </object>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                      />

                      {!cameraOpen && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-950 text-center text-xs text-white/70">
                          {previewLoading
                            ? t("scannerPage.loadingPreview")
                            : cameraStarting
                              ? t("scannerPage.startingCamera")
                              : t("scannerPage.cameraPreview")}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center justify-center gap-2 bg-white p-3">
                  {hasPreview ? (
                    <button
                      type="button"
                      onClick={retakePhoto}
                      className={`${A.btnBase} ${A.btnSecondary} min-w-[120px]`}
                      disabled={uploading || reading || cameraStarting}
                    >
                      {t("scannerPage.retake")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={capturePhoto}
                      className={`${A.btnBase} ${A.btnSecondary} min-w-[140px]`}
                      disabled={!cameraOpen || uploading || reading}
                    >
                      {uploading ? t("scannerPage.uploading") : t("scannerPage.capture")}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4">
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={onFileChange}
                  className="hidden"
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-[13px] font-medium text-gray-700">
                      {selectedFile ? selectedFile.name : t("scannerPage.uploadFormats")}
                    </div>
                    {cameraUnavailable || selectedFile ? (
                      <div className="mt-1 text-xs text-gray-500">
                        {cameraUnavailable
                          ? t("scannerPage.cameraUnavailableHint")
                          : selectedFile?.type || t("scannerPage.selectedReceipt")}
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => uploadInputRef.current?.click()}
                    className={`${A.btnBase} ${A.btnSecondary}`}
                    disabled={uploading || reading || planLoading}
                  >
                    {uploading ? t("scannerPage.uploading") : t("scannerPage.chooseFile")}
                  </button>
                </div>
                {hasPreview && (
                  <div className="scanner-preview-boundary mt-4 rounded-2xl border border-gray-200 bg-white">
                    {isImage ? (
                      <img
                        src={previewUrl}
                        alt={t("scannerPage.receiptPreview")}
                        className="max-h-[520px] w-full object-contain"
                      />
                    ) : isPdf ? (
                      <object
                        data={previewUrl}
                        type="application/pdf"
                        className="h-[520px] w-full"
                      >
                        <div className="p-4 text-xs text-gray-500">
                          {t("purchases.detail.pdfUnavailable")}{" "}
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-appleblue hover:underline"
                          >
                            {t("scannerPreview.openPdf")}
                          </a>
                        </div>
                      </object>
                    ) : null}
                  </div>
                )}
                {previewLoading && (
                  <div className="mt-3 text-center text-xs text-gray-500">
                    {t("scannerPage.loadingPreview")}
                  </div>
                )}
              </div>
            )}
          </section>

          <div className="hidden h-12 items-center lg:flex">
            <div className="w-full border-t border-gray-100" />
          </div>

          <section
            className={`${A.card} p-4 lg:rounded-none lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[13px] font-medium text-gray-600">
                  {t("scannerPage.suggested")}
                </h2>
                {(reading || sourceDocument) && (
                  <p className="mt-1 text-xs text-gray-500">
                    {reading
                      ? t("scannerPage.readingDocument")
                      : t("scannerPage.reviewDocument")}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1.5 block text-xs text-gray-500">
                  {t("directory.vendors.singular")}
                </span>
                <input
                  type="text"
                  value={vendorDraft}
                  onChange={(event) => setVendorDraft(event.target.value)}
                  placeholder={reading ? t("scannerPage.reading") : t("scannerPage.vendorName")}
                  disabled={!suggestion || reading}
                  className={`${A.input} w-full rounded-xl text-[13px] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400`}
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs text-gray-500">{t("sales.common.date")}</span>
                <AppleDateInput
                  openCalendarLabel={t("sales.shared.openCalendar")}
                  valueYmd={dateDraftYmd}
                  onChangeYmd={setDateDraftYmd}
                  allowEmpty
                  ariaLabel={t("sales.common.date")}
                  placeholder={reading ? t("scannerPage.reading") : t("scannerPage.datePlaceholder")}
                  disabled={!suggestion || reading}
                  inputClassName="w-full rounded-xl text-[13px] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs text-gray-500">
                  {t("sales.common.amount")}
                </span>
                <div
                  className={`${A.fieldBase} flex h-10 overflow-hidden bg-white p-0 focus-within:border-appleblue/50 focus-within:ring-2 focus-within:ring-appleblue/20`}
                >
                  <span className="flex shrink-0 items-center pl-3 text-[13px] font-normal text-gray-500">
                    {companyCurrency}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={t("scannerPage.amountInCurrency", { currency: companyCurrency })}
                    value={amountDraft}
                    onChange={(event) => setAmountDraft(event.target.value)}
                    placeholder={reading ? t("scannerPage.reading") : "0.00"}
                    disabled={!suggestion || reading}
                    className="ios-no-zoom h-full min-w-0 flex-1 bg-transparent px-3 text-right text-[13px] tabular-nums text-gray-700 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>
                {showFxNote && (
                  <span className="mt-1.5 block text-xs leading-5 text-gray-500">
                    {t("scannerPage.fxNote", { detectedCurrency, companyCurrency })}
                  </span>
                )}
              </label>
            </div>

            {!!suggestion && (
              <details className="group mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-white">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3.5 py-3 text-[13px] font-medium text-gray-700 marker:content-none">
                  <span>{t("scannerPage.viewScannedText")}</span>
                  <span className="text-xs font-normal text-gray-400 group-open:hidden">
                    {t("scannerPage.rawOcrText")}
                  </span>
                  <span className="hidden text-xs font-normal text-gray-400 group-open:inline">
                    {t("scannerPage.hide")}
                  </span>
                </summary>
                <div className="border-t border-gray-100 px-3.5 py-3">
                  {rawOcrText ? (
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-gray-600">
                      {rawOcrText}
                    </pre>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {t("scannerPage.noScannedText")}
                    </p>
                  )}
                </div>
              </details>
            )}
          </section>

          <div className="hidden h-12 items-center lg:flex">
            <div className="w-full border-t border-gray-100" />
          </div>

          <section className="hidden lg:block">
            <h2 className="text-[15px] font-semibold text-gray-700">{t("scannerPage.target")}</h2>

            <div className="mt-3 grid grid-cols-2 gap-2">
              {(["expense", "purchase"] as ScannerTarget[]).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => setTarget(kind)}
                  aria-pressed={target === kind}
                  className={[
                    "h-10 rounded-xl border px-3 text-[13px] font-medium capitalize transition",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                    target === kind
                      ? "border-appleblue bg-appleblue/10 text-appleblue"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
                  ].join(" ")}
                >
                  {kind === "expense" ? t("scannerPage.expenseTarget") : t("scannerPage.purchaseTarget")}
                </button>
              ))}
            </div>
          </section>

          <div className="hidden h-12 items-center lg:flex">
            <div className="w-full border-t border-gray-100" />
          </div>

          <section
            className={`${A.card} p-4 lg:rounded-none lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <h2 className="text-[13px] font-medium text-gray-600 lg:hidden">
              {t("scannerPage.createFromScan")}
            </h2>
            <h2 className="hidden text-[13px] font-medium text-gray-600 lg:block">
              {t("scannerPage.createFromScan")}
            </h2>

            <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden">
              <button
                type="button"
                onClick={() => openAccountingEntry("/expenses/new")}
                disabled={!scannerHandoff}
                title={
                  scannerHandoff
                    ? t("scannerPage.reviewExpense")
                    : t("scannerPage.completeFields")
                }
                className={`${A.btnBase} ${A.btnPrimary} w-full rounded-xl disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {t("scannerPage.enterExpense")}
              </button>
              <button
                type="button"
                onClick={() => openAccountingEntry("/purchases/new")}
                disabled={!scannerHandoff}
                title={
                  scannerHandoff
                    ? t("scannerPage.reviewPurchase")
                    : t("scannerPage.completeFields")
                }
                className={`${A.btnBase} ${A.btnSecondary} w-full rounded-xl disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {t("scannerPage.enterPurchase")}
              </button>
            </div>

            <button
              type="button"
              onClick={() =>
                openAccountingEntry(
                  target === "expense" ? "/expenses/new" : "/purchases/new",
                )
              }
              disabled={!scannerHandoff}
              title={
                scannerHandoff
                  ? target === "expense" ? t("scannerPage.reviewExpense") : t("scannerPage.reviewPurchase")
                  : t("scannerPage.completeFields")
              }
              className={`${A.btnBase} ${A.btnPrimary} mt-3 hidden w-full rounded-xl disabled:cursor-not-allowed disabled:opacity-50 lg:inline-flex`}
            >
              {target === "expense" ? t("scannerPage.continueExpense") : t("scannerPage.continuePurchase")}
            </button>
          </section>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-medium text-red-700 lg:mt-4">
              {typeof error === "string" ? error : t(error.key)}
            </div>
          )}
        </div>
      </div>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
